import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ScopedPage } from '@/layouts/ScopedPage';
import { hasCapability, useProjectScope } from '@/context/ProjectContext';
import { useCreatePipelineRunMutation, useUploadRequirementMutation } from '@/services/api';
import { parseApiError } from '@/utils/apiError';
import { useToast } from '@/components/toast/ToastProvider';
import {
  combineRequirement,
  DEFAULT_WORKFLOW_ID,
  formatBytes,
  MAX_REQUIREMENT_FILE_BYTES,
  uploadReasonMessage
} from '@/utils/runSubmission';
import type { RequirementUploadReason } from '@/types/api';

export function NewRunPage(): JSX.Element {
  const { project, user } = useProjectScope();

  if (!hasCapability(user, 'pipeline.create')) {
    return (
      <ScopedPage title="New run">
        <h1 className="page-title">New run</h1>
        <div className="panel" style={{ borderLeft: '3px solid var(--orange)' }}>
          <h2 className="panel-title" style={{ margin: '0 0 4px' }}>
            Permission required
          </h2>
          <p className="panel-sub" style={{ margin: 0 }}>
            You don&apos;t have permission to submit runs in this project — contact your administrator. The
            &quot;New run&quot; action is hidden from the dashboard when you lack this capability.{' '}
            <Link to={`/p/${project.slug}/dashboard`}>Back to dashboard</Link>
          </p>
        </div>
      </ScopedPage>
    );
  }

  return (
    <ScopedPage title="New run" extraCrumbs={[{ label: 'Pipeline runs', to: `/p/${project.slug}/dashboard` }]}>
      <h1 className="page-title">New run</h1>
      <NewRunForm />
    </ScopedPage>
  );
}

function NewRunForm(): JSX.Element {
  const navigate = useNavigate();
  const { project } = useProjectScope();
  const toast = useToast();
  const [createRun, { isLoading: isSubmitting }] = useCreatePipelineRunMutation();
  const [uploadRequirement, { isLoading: isUploading }] = useUploadRequirementMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [requirement, setRequirement] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [attached, setAttached] = useState<
    ReadonlyArray<{
      name: string;
      size: number;
      extracted_text: string;
      upload_id: string;
      char_count: number;
    }>
  >([]);

  const hasFileText = attached.some((a) => a.extracted_text.trim() !== '');
  const hasRequirement = requirement.trim() !== '' || hasFileText;
  const canSubmit = hasRequirement && !isSubmitting && !isUploading;

  async function onFileSelected(file: File): Promise<void> {
    setFileError(null);
    if (file.size > MAX_REQUIREMENT_FILE_BYTES) {
      setFileError(`${file.name}: ${uploadReasonMessage('file_too_large')}`);
      return;
    }
    if (attached.some((a) => a.name === file.name && a.size === file.size)) return;
    try {
      const res = await uploadRequirement({ projectId: project.project_id, file }).unwrap();
      setAttached((prev) => [
        ...prev,
        {
          name: file.name,
          size: file.size,
          extracted_text: res.extracted_text,
          upload_id: res.upload_id,
          char_count: res.char_count
        }
      ]);
    } catch (err) {
      const parsed = parseApiError(err);
      const reason = (parsed.code === 'VALIDATION_ERROR' && parsed.field) || '';
      const known: RequirementUploadReason[] = [
        'unsupported_file',
        'file_too_large',
        'extraction_failed',
        'password_protected',
        'macro_enabled',
        'corrupted_file'
      ];
      const msg = known.includes(reason as RequirementUploadReason)
        ? uploadReasonMessage(reason as RequirementUploadReason)
        : uploadReasonMessage('extraction_failed');
      setFileError(`${file.name}: ${msg}`);
    }
  }

  async function onFilesSelected(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      await onFileSelected(file);
    }
  }

  function removeFileAt(index: number): void {
    setAttached((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    const combined = combineRequirement(requirement, attached);
    try {
      const res = await createRun({
        project_id: project.project_id,
        workflow_id: DEFAULT_WORKFLOW_ID,
        inputs: {
          user_task_description: combined,
          ...(attached.length > 0
            ? { requirement_upload_ids: attached.map((a) => a.upload_id) }
            : {})
        }
      }).unwrap();
      toast.success('Run submitted.');
      navigate(`/p/${project.slug}/runs/${encodeURIComponent(res.pipeline_run_id)}`);
    } catch (err) {
      const parsed = parseApiError(err);
      if (parsed.code === 'FORBIDDEN') {
        setSubmitError("You don't have permission to submit runs in this project — contact your administrator.");
      } else {
        setSubmitError(parsed.message);
      }
    }
  }

  return (
    <>
      <form className="panel" style={{ maxWidth: 680 }} onSubmit={onSubmit}>
        {submitError && (
          <div className="field-err" style={{ margin: '0 0 18px' }} role="alert" data-testid="nr-submit-error">
            {submitError} Your entries are preserved.
          </div>
        )}

        <div className="field">
          <label className="field-label" htmlFor="nr-project">
            Project
          </label>
          <input
            id="nr-project"
            className="input mono readonly"
            value={project.slug}
            readOnly
            disabled
            tabIndex={-1}
            aria-readonly="true"
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="nr-req">
            Requirement <span style={{ color: 'var(--red)' }}>*</span>
          </label>
          <textarea
            id="nr-req"
            className="textarea"
            style={{ minHeight: 110 }}
            placeholder="Describe what to build. Prose, bullet points, or pasted acceptance criteria + constraints all work — it's read as a single free-form instruction."
            value={requirement}
            onChange={(e) => setRequirement(e.target.value)}
          />
        </div>

        <div className="nr-or">and / or attach a file</div>

        <div className="field">
          <label className="field-label">Requirement files</label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            data-testid="nr-file-input"
            style={{ display: 'none' }}
            onChange={(e) => {
              void onFilesSelected(e.target.files);
            }}
          />

          <div
            className="dropzone"
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void onFilesSelected(e.dataTransfer.files);
            }}
          >
            <span className="dz-icon">⤓</span>
            <span className="dz-title">
              {isUploading
                ? 'Extracting…'
                : attached.length > 0
                  ? 'Drag more files here, or '
                  : 'Drag files here, or '}
              {!isUploading && <span className="dz-browse">browse</span>}
            </span>
            <span className="dz-formats mono">docx · pdf · md · txt · rtf · xlsx · xls · csv · max 5&nbsp;MB each</span>
          </div>

          {fileError && (
            <div className="field-err" style={{ marginTop: 0 }} role="alert" data-testid="nr-file-error">
              {fileError}
            </div>
          )}

          {attached.length > 0 && (
            <div className="nr-file-list" data-testid="nr-file-list">
              {attached.map((a, i) => (
                <div className="file-chip" data-testid="nr-file-chip" key={`${a.name}:${a.size}:${i}`}>
                  <span className="fc-icon mono">{extOf(a.name)}</span>
                  <div className="fc-meta">
                    <div className="fc-name">{a.name}</div>
                    <div className="fc-sub mono">
                      {formatBytes(a.size)} ·{' '}
                      <span className="fc-ok">✓ extracted ({a.char_count.toLocaleString()} chars)</span>
                    </div>
                  </div>
                  <button type="button" className="btn ghost sm" onClick={() => removeFileAt(i)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <Link className="btn ghost" to={`/p/${project.slug}/dashboard`}>
            Cancel
          </Link>
          <button className="btn primary" type="submit" disabled={!canSubmit} data-testid="nr-submit">
            {isSubmitting ? 'Submitting…' : 'Submit run'}
          </button>
        </div>
      </form>
    </>
  );
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toUpperCase().slice(0, 4) : 'FILE';
}
